import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { MouseEvent, useState } from 'react';

interface MenuOption {
  label: string;
  onClick: (_rowData?: any) => void;
}

interface CustomActionButtonProps {
  menuOptions?: MenuOption[];
  buttonSx?: SxProps<Theme>;
  height?: string | number;
  rowData?: any;
}

const CustomActionButton: React.FC<CustomActionButtonProps> = ({
  menuOptions = [],
  buttonSx,
  height = '100%',
  rowData,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // Prevent bubbling to AG Grid row/cell click
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        height: height,
        justifyContent: 'center',
        alignItems: 'center',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <IconButton
          size="small"
          disableRipple
          sx={{
            ...buttonSx,
            '& :hover': { backgroundColor: theme.palette.menuIconHoverBg },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e as any);
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MoreVertIcon
            sx={{
              color: theme.palette.menuIconColor,
              fontSize: '15px',
              padding: '8px',
              borderRadius: '50%',
            }}
          />
        </IconButton>
      </Box>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(e) => {
          // stop bubbling on close interactions as well
          // Note: MUI passes reason as second param in some variants; typing kept simple
          (e as any)?.stopPropagation?.();
          handleClose();
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
        sx={{
          '& .MuiMenu-list': {
            padding: 0,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuOptions?.map((option, index) => (
          <MenuItem
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              option?.onClick(rowData);
              handleClose();
            }}
            sx={{
              fontSize: '14px',
              minWidth: '100px',
              '&:hover': {
                backgroundColor: theme.palette.menuItemHoverBg,
                color: theme.palette.primary.main,
              },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {option?.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default CustomActionButton;
